import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Form } from "react-bootstrap";
import { useLoader } from '../../_helper/LoaderContext';
import { addGlobalUnit, addUnit, getGlobalUnitList, updateUnit } from './service/unit-service';
import { toast } from 'react-toastify';
import Select, { components } from 'react-select';
import { Input, Label } from 'reactstrap';

// Custom Checkbox Option
const Option = (props) => {
    return (
        <components.Option {...props}>
            <div className="d-flex align-items-center py-1">
                <input
                    type="checkbox"
                    checked={props.isSelected}
                    onChange={() => null}
                    className="me-2"
                    style={{ pointerEvents: 'none' }}
                />
                <label className="m-0 cursor-pointer w-100">{props.label}</label>
            </div>
        </components.Option>
    );
};
// Select All Option
const allOption = { label: "Select All", value: "*" };

const customStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: '44px',
        height: '44px',
        overflow: 'hidden',
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: '44px',
        padding: '0 8px',
        overflowX: 'auto',        // This enables horizontal scroll
        overflowY: 'hidden',
        flexWrap: 'nowrap',       // Important: prevents wrapping to new line
        whiteSpace: 'nowrap',
        scrollbarWidth: 'thin',   // Firefox
        '&::-webkit-scrollbar': {
            height: '6px',
        },
        '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
        },
    }),
    multiValue: (styles) => ({
        ...styles,
        backgroundColor: '#1b255b',
        margin: '2px',
        flexShrink: 0,  // Prevents shrinking
    }),
    multiValueLabel: (styles) => ({
        ...styles,
        color: '#fff',
        fontWeight: 500,
    }),
    multiValueRemove: (styles) => ({
        ...styles,
        color: '#d32f2f',
        ':hover': {
            backgroundColor: '#ffcdd2',
            color: 'white',
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
    }),
};



export default function AddUnit({ handleClose, data, getList }) {
    const { t } = useTranslation();

    // State for both name and description
    const [unitName, setUnitName] = useState("");
    const [description, setDescription] = useState("");
    const [error, setError] = useState("");
    const [imported, setImported] = useState("add")
    const { setLoading } = useLoader();
    const [unitMaster, setUnitMaster] = useState([]);
    const [selectedGlobalUnits, setSelectedGlobalUnits] = useState([]);
    // Populate fields when editing
    useEffect(() => {
        if (data) {
            setUnitName(data.name || "");
            setDescription(data.description || ""); // assuming backend sends "description"
        } else {
            // Reset when adding new
            setUnitName("");
            setDescription("");
        }
    }, [data]);

    const handleSubmit = async () => {
        if (!unitName.trim()) {
            setError(t("Please enter unit name is required"));
            return;
        }

        setLoading(true);
        try {
            const form_data = new FormData();
            form_data.append("name", unitName.trim());
            form_data.append("description", description.trim()); // send description

            let res;
            if (data) {
                form_data.append("unitId", data._id);
                res = await updateUnit(form_data);
            } else {
                res = await addUnit(form_data);
            }

            if (res.data.success) {
                toast.success(res.data.message);
                getList(); // refresh list
                handleClose();
            }
        } catch (error) {
            console.error("Error saving unit:", error);
            // toast.warning(error?.response?.data?.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const handleRadio = (e) => {
        const value = e.target.value;
        setImported(value);
    };

    // Load global units once
    useEffect(() => {
        getGlobalUnit();
    }, []);

    // Fetch global units
    const getGlobalUnit = async () => {
        setLoading(true);
        try {
            const res = await getGlobalUnitList();
            if (res.data.success) {
                setUnitMaster(res.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching global units:", error);
        } finally {
            setLoading(false);
        }
    };

    const allOption = useMemo(() => ({ label: t("Select All"), value: "*" }), [t]);

    // Transform unitMaster into react-select format
    const unitOptions = useMemo(() => {
        const options = unitMaster.map(u => ({
            label: u.name,
            value: u._id,
            description: u.description || ""
        }));
        return unitMaster.length > 0 ? [allOption, ...options] : [];
    }, [unitMaster, allOption]);

    // Handle Select Change with "Select All" logic
    const handleGlobalUnitChange = (selected) => {
        // "selected" comes from react-select (may include "Select All")
        if (!selected) {
            setSelectedGlobalUnits([]);
            return;
        }

        const hasSelectAll = selected.some(opt => opt.value === "*");

        if (hasSelectAll) {
            // Toggle Select All
            if (selectedGlobalUnits.length === unitMaster.length) {
                setSelectedGlobalUnits([]); // Deselect all
            } else {
                // Select all real units
                setSelectedGlobalUnits(unitMaster.map(u => ({
                    label: u.name,
                    value: u._id
                })));
            }
        } else {
            // Normal multi-select behavior
            // Filter out the "Select All" option from value
            const realSelections = selected.filter(opt => opt.value !== "*");
            setSelectedGlobalUnits(realSelections);
        }
    };

    // Submit selected global units
    const handleSubmitSelected = async () => {
        const selectedIds = selectedGlobalUnits
            .filter(item => item.value !== "*")
            .map(item => item.value);

        if (selectedIds.length === 0) {
            toast.info(t("No units selected"));
            return;
        }
        const payload = {
            masterUnitIds: selectedIds
        }
        setLoading(true)
        try {
            const res = await addGlobalUnit(payload);
            if (res.data.success == true) {
                toast.success(res.data.message);
                getList();
                setSelectedGlobalUnits([])
                handleClose()
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false)
        }
    };

    return (
        <>
            <Modal.Header closeButton className="py-2 px-3 border-bottom">
                <Modal.Title className="fs-6 fw-semibold m-0">
                    {data ? t("Edit Unit") : t("Add Unit")}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="py-2 px-3">
                {data ? (
                    <></>
                ) : (
                    <div className="d-flex tw-space-x-4 mb-2">
                        <div className="radio radio-primary">
                            <Input
                                id="customer-radio-add"
                                type="radio"
                                name="customerType"
                                value="add"
                                checked={imported === "add"}
                                onChange={handleRadio}
                            />
                            <Label for="customer-radio-add" className="mb-0 pe-2">
                                {t("Manual")}
                            </Label>
                        </div>
                        <div className="radio radio-primary">
                            <Input
                                id="customer-radio-gloabal"
                                type="radio"
                                name="customerType"
                                value="gloabal"
                                checked={imported === "gloabal"}
                                onChange={handleRadio}
                            />
                            <Label for="customer-radio-gloabal" className="mb-0 pe-2">
                                {t("Import From Database")}
                            </Label>
                        </div>
                        {selectedGlobalUnits.length > 0 && (
                            <Button
                                size="sm"
                                onClick={handleSubmitSelected}
                                disabled={selectedGlobalUnits.length === 0}
                            >
                                {t("Import")} ({selectedGlobalUnits.length})
                            </Button>
                        )}
                    </div>
                )}
                {imported === "add" ? (
                    <>
                        <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                            {/* Unit Name */}
                            <Form.Group className="mb-2" controlId="unitName">
                                <Form.Label className="form-label mb-1" style={{ fontSize: "12.5px", fontWeight: 500 }}>
                                    {t("Unit Name")} <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    size="sm"
                                    placeholder={t("Enter unit name")}
                                    value={unitName}
                                    onChange={(e) => {
                                        setUnitName(e.target.value);
                                        setError("");
                                    }}
                                    isInvalid={!!error}
                                    autoFocus
                                    style={{ height: "36px", fontSize: "13px" }}
                                />
                                <Form.Control.Feedback type="invalid" style={{ fontSize: "11px", marginTop: "2px" }}>
                                    {error}
                                </Form.Control.Feedback>
                            </Form.Group>

                            {/* Description */}
                            <Form.Group controlId="description" className="mb-0">
                                <Form.Label className="form-label mb-1" style={{ fontSize: "12.5px", fontWeight: 500 }}>{t("Description")} ({t("optional")})</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    size="sm"
                                    placeholder={t("Enter description (e.g., mg/dL, mmol/L, etc.)")}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    style={{
                                        height: "56px",
                                        minHeight: "56px",
                                        maxHeight: "72px",
                                        fontSize: "13px",
                                        resize: "none",
                                    }}
                                />
                            </Form.Group>
                        </Form>
                        <Modal.Footer className="py-2 px-0 border-top mt-2 d-flex justify-content-end gap-2">
                            {imported === "gloabal" ? (
                                <Button
                                    size="sm"
                                    className="px-3"
                                    onClick={handleSubmitSelected}
                                    disabled={selectedGlobalUnits.length === 0}
                                >
                                    {t("Import")} ({selectedGlobalUnits.length})
                                </Button>
                            ) : (
                                <Button variant="primary" size="sm" className="px-3" onClick={handleSubmit}>
                                    {data ? t("Update") : t("Create")}
                                </Button>
                            )}
                            <Button variant="secondary" size="sm" className="px-3" onClick={handleClose}>
                                {t("Close")}
                            </Button>

                        </Modal.Footer>
                    </>
                ) : (
                    <Select
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        options={unitOptions}
                        value={selectedGlobalUnits}
                        onChange={handleGlobalUnitChange}
                        placeholder={t("Add units from global list…")}
                        components={{ Option }}
                        styles={customStyles}
                        classNamePrefix="react-select"
                        isClearable={true}
                    />
                )}

            </Modal.Body>


        </>
    );
}